/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {ColDef, ColumnState} from 'ag-grid-community';
import debounce from 'lodash.debounce';
import {useSystemOfUnits} from '@ion/applications/src/hooks/useSystemOfUnits';
import {translate} from '@ion/core/src/locale/locale';
import Device from '@ion/core/src/services/Device';
import {getEventList} from '@ion/core/src/services/EventCodes';
import {isMetric} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {dateSortComparator} from '@ion/core/src/utils/SortUtils';
import {LinkCell} from '@ion/dls/src/components/grid/LinkCell';
import {
  caseInSensitiveEqualsFilterParams,
  defaultCVSFilterParams,
  defaultDateFilterParams,
  defaultTextContainsFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {
  definitionsToState, resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  AgGridType, IonGrid, IonGridAPI, RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  AG_CLASSES,
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  useColumnChooser
} from '@ion/dls/src/grid/useColumnChooser';
import {
  useColumnState
} from '@ion/dls/src/grid/useColumnState';
import {useLoadGridData} from '@ion/dls/src/grid/useLoadGridData';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {DLSTheme} from '@ion/dls/src/types/types';
import {onAssetRowClicked} from '@ion/dls/src/utils/GridUtils';
import {exportTrackingAssets} from '@/store/actions/exporter';
import {dispatchGridColumnsChange} from '@/store/actions/helpers';
import {
  attributeMappings,
  getCVSPaginatedAssets,
  getLatestAssetData,
  NAME,
  onAssetSelected,
  RESET_FETCH_LARGE_FLEET_ASSETS,
  SET_GRID_COLUMNS,
  setFilterModel,
  setGridChange,
  setIsAssetGridUpdate,
  setLargeFleetGridCount,
  setSelectedAsset
} from '@/store/actions/tracking';
import {
  desktopTopSheetOpenSelector
} from '@/store/selectors/search-selectors';
import {
  getListAssetsSelector,
  gridColumnStateSelector,
  gridFilterModelSelector,
  isLargeFleetModeSelector,
  largeFleetAssetCountSelector,
  selectedAssetSelector
} from '@/store/selectors/tracking-selectors';
import {useAssetListState} from '../../hooks/useAssetListState';
import AssetTypeCell from '../admin/assets/AssetTypeCell';
import {SpeedCell} from '../admin/assets/SpeedCell';
import {constructCVSGridRows} from '../tracking/TrackingAssetClientGrid';
import {useScrollToItem} from './hooks/grid/useScrollToItem';
import useTrackingAssetCount from './hooks/sidesheet/useTrackingAssetCount';
import {
  columnResolver,
  filterModelConverterV2,
  scrollToInterval,
  sortModelConverter
} from './trackingUtils';

// prop definitions for asset history grid component.
export interface TrackingAssetServerGridProps {
  assets?: Device[];
  isLargeFleetMode?: boolean;
  height?: number;
  includesAssets?: boolean;
  includesScDevices?: boolean;
  isFilterSelected?: boolean;
  isDesktopTopSheetOpen?: boolean;
};

// would like to sort by assignment, unchecked at top
const defaultSort: SortModelType = {
  colId : 'assetNameUI',
  sort  : 'asc'
};

interface TrackingGridStyleProps {
  height?: number;
  isDesktopTopSheetOpen?: boolean;
  isFilterSelected?: boolean;
};

export const useTrackingGridStyles = makeStyles<DLSTheme, TrackingGridStyleProps>((theme: DLSTheme) => ({
  agGridAssetTracking : {
    height                : '100%',
    '& .ag-body-viewport' : {
      minHeight : ({height}: TrackingGridStyleProps) => `${height ? 0 : 150}px !important`
    },
    '&&' : {
      [AG_CLASSES.BODY_LAYOUT] : {
        overflowY : 'auto',
        minHeight : ({height}: TrackingGridStyleProps) => `${height ? 0 : 150}px !important`,
        height    : ({height}: TrackingGridStyleProps) => `${height}px !important`
      }
    },
    [theme.breakpoints.up('md')] : {
      [AG_CLASSES.BODY_LAYOUT] : {
        maxHeight : ({isDesktopTopSheetOpen, isFilterSelected}: TrackingGridStyleProps) => {
          if (isDesktopTopSheetOpen) {
            return isFilterSelected ? 'calc(100vh - 310px)' : 'calc(100vh - 250px)';
          } else {
            return isFilterSelected ? 'calc(100vh - 250px)' : 'calc(100vh - 200px)';
          }
        }
      }
    },
    [theme.breakpoints.down('sm')] : {
      [AG_CLASSES.BODY_LAYOUT] : {
        maxHeight : '80vh'
      }
    }
  }
}));

/**
   * @TrackingAssetServerGrid
   *
   * Grid to be rendered on the Admin Roles page.
   */
export const TrackingAssetServerGrid = ({
  height,
  isFilterSelected = false
}: TrackingAssetServerGridProps, ref) => {
  const dispatch                              = useDispatch();
  const {isDesktop, isPhone}                  = useMedia();
  const mediaRef                              = useRef({isDesktop, isPhone});
  const eventListRef                          = useRef(getEventList());
  const ionGridRef                            = useRef<IonGridAPI>();
  const [gridApi, setGridApi]                 = useState<AgGridType>(null);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const showAssetsListRef                     = useAssetListState();
  const columnState: ColumnState[]            = useSelector(gridColumnStateSelector);
  const selectedAsset                         = useSelector(selectedAssetSelector);
  const filterModel                           = useSelector(gridFilterModelSelector);
  const isLargeFleetMode                      = useSelector(isLargeFleetModeSelector);
  const isDesktopTopSheetOpen                 = useSelector(desktopTopSheetOpenSelector);
  const largeFleetAssetCount                  = useSelector(largeFleetAssetCountSelector);
  const onLoading                             = useTrackingAssetCount();
  const unitsInHeader                         = useSystemOfUnits();
  const {
    loading,
    includesAssets,
    includesScDevices
  } = useSelector(getListAssetsSelector);

  const classes                               = useTrackingGridStyles({
    height,
    isDesktopTopSheetOpen,
    isFilterSelected
  });
  const rowClickDispatchFn                    = useCallback(async(data) => {
    if (data) {
      const asset = await getLatestAssetData({selectedAsset: data, includeTime: false});
      dispatch(setSelectedAsset({
        asset,
        useCtc      : !showAssetsListRef.current,
        resetTool   : true,
        coordinates : null
      }) as any);
    }
  }, []);
  const {
    rowClicked,
    scrollToItem
  } = useScrollToItem(gridApi, scrollToInterval.serverMode);
  const onRowClickHandler = onAssetRowClicked(rowClickDispatchFn, rowClicked);

  useEffect(() => {
    mediaRef.current = {isDesktop, isPhone};
  }, [isDesktop, isPhone]);

  // Set the grid API so we can use it to find rows that are clicked outside the grid.
  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  const onFilterChanged = useCallback(function() {
    if (gridApi) {
      const filterModel = gridApi?.getFilterModel();
      batch(() => {
        dispatch(setFilterModel(filterModel));
        dispatch(setIsAssetGridUpdate(true) as any);
        dispatch(setGridChange() as any);
      });
    }
  }, [gridApi]);

  const getColumnDefinitions = (includesAssets, includesScDevices, unitsInHeader) => ({
    desktop : resolveDefinitions(getColumnDefinitionsDesktop(includesAssets, includesScDevices, unitsInHeader)),
    mobile  : resolveDefinitions(getColumnDefinitionsMobile(unitsInHeader))
  });

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(includesAssets, includesScDevices, unitsInHeader),
    resolver          : columnResolver,
    columnState
  });

  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    onExport,
    onRefresh,
    onRefreshData,
    onClearFilters
  }));

  const onExport = function() {
    const params = ionGridRef.current?.getExportParams();
    if (!isLargeFleetMode) {
      const {items} = ionGridRef.current?.getAllDisplayedRowData();
      dispatch(exportTrackingAssets({
        ...params,
        items,
        augmenter : constructAssetHistoryGridRows,
        largeFleetAssetCount
      }) as any);
    } else {
      dispatch(exportTrackingAssets({
        ...params,
        augmenter : constructAssetHistoryGridRows,
        largeFleetAssetCount
      }) as any);
    }
  };

  const onClearFilters = useCallback(() => {
    ionGridRef.current?.onClearFilters();
  }, [ionGridRef]);

  const onRefreshData = useCallback(() => {
    gridApi?.purgeInfiniteCache();
  }, []);

  const onRefresh = function() {
    setRefreshRequired(true);
  };

  const onColumnSelectionsUpdate = useCallback(function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS) as any);
  }, []);

  const onColumnStateUpdate = useCallback(function(state: ColumnState[]): void {
    state && dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS) as any);
  }, []);

  const getAssets = useCallback(debounce(({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) => {
    batch(() => {
      dispatch({type: RESET_FETCH_LARGE_FLEET_ASSETS});
      dispatch(getCVSPaginatedAssets({
        startRow : page * pageSize,
        pageSize,
        sort,
        filter,
        callback
      }) as any);
    });
  }, 350), []);

  const getAssetData = useCallback(useLoadGridData({
    loadData : getAssets,
    filterModel,
    gridApi
  }), []);

  const constructAssetHistoryGridRows = useCallback(function(items): GridResult[] {
    return items?.map(item => constructCVSGridRows(item, eventListRef));
  }, []);

  /**
   * @private
   */
  const onAssetClick = useCallback(async({data}) => {
    if (data) {
      const device = Device.getAssetsFromCollection([data])[0];
      const asset  = await getLatestAssetData({selectedAsset: device, includeTime: false, returnAsset: true});
      dispatch(onAssetSelected({asset, media: mediaRef.current, resetTool: true}) as any);
    }
  }, []);

  const setHFMGridCount = useCallback((number) => {
    dispatch(setLargeFleetGridCount(number));
  }, []);

  const customScrollTop: string = useMemo(() => {
    return isDesktop
      ? `${(document.getElementById('trackingFilterWrapper') as HTMLElement)?.offsetHeight}px`
      : '0px';
  }, []);

  const getGridHeight = useCallback(() => {
    return classes.agGridAssetTracking;
  }, []);

  const getRowNodeId = useCallback(({data}) => {
    return String(data?.isScDevice ? data?.deviceIdValue : data?.assetId);
  }, []);

  const onColumnChooserClose = useCallback(() => {
    toggleColumnChooser(false);
  }, []);

  const selectAndScrollToRow = useCallback(debounce((gridApi) => {
    scrollToItem?.(gridApi, selectedAsset);
  }, scrollToInterval.serverMode), [selectedAsset]);

  return (
    <>
      <IonGrid ref={ionGridRef}
        onLoadingChange={onLoading}
        suppressInfiniteLoading={true}
        usePreferedPageSize={false}
        onGridReady={onGridReady}
        onFirstDataRendered={function(event) {
          selectAndScrollToRow?.(event.api);
        }}
        customScrollTop={customScrollTop}
        filterModelConverter={filterModelConverterV2}
        sortModelConverter={sortModelConverter}
        refreshRequired={refreshRequired}
        setRefreshRequired={setRefreshRequired}
        getRowNodeId={getRowNodeId}
        getGridHeight={getGridHeight}
        onRowClicked={onRowClickHandler}
        loadItems={getAssetData}
        setCount={setHFMGridCount}
        onFilterChanged={onFilterChanged}
        augmentItems={constructAssetHistoryGridRows}
        attributeMappings={attributeMappings}
        defaultSort={defaultSort}
        showColumnChooser={showColumnChooser}
        columnDefinitions={columnDefinitions}
        onColumnChooserSave={onColumnSelectionsUpdate}
        onColumnsUpdated={onColumnStateUpdate}
        onColumnChooserClose={onColumnChooserClose}
        rowMenusEnabled={false}
        defaultDefinitions={defaultDefinitions}
        filterModel={filterModel}
        pageSize={100}
        rowBuffer={10}
        columnComponents={{
          assetType : AssetTypeCell,
          speedCell : ({data: {heading, speedUI} = {}}: {data: any}) => (
            <SpeedCell heading={heading} value={speedUI} />
          ),
          gpsSpeedCell : ({data: {heading, gpsSpeedUI} = {}}: {data: any}) => (
            <SpeedCell heading={heading} value={gpsSpeedUI} />
          ),
          vbSpeedCell : ({data: {heading, vbSpeedUI} = {}}: {data: any}) => (
            <SpeedCell heading={heading} value={vbSpeedUI} />
          ),
          assetName : (data: any) => (
            <LinkCell data = {data}
              onClick={onAssetClick} />
          ),
          eventTimeUI : ({data}) => data?.eventTimeUI || null
        }}
        checkboxesEnabled={false}
        omitHeader />
    </>
  );
};

/**
 * constructCVSGridRows formats the ArcGIS raw data into user
 * readable format/form.
 */

/**
   * @getColumnDefinitionsDesktop
   *
   * Returns column definitions for the desktop grid implementation ...
   */
const getColumnDefinitionsDesktop = (includesAssets: boolean, includesScDevices: boolean,
  unitsInHeader: Unit): ColDef[] => {
  const getUOMColumn = (units: Unit, metric: string, imperial: string): string => (
    translate(`tracking:tableView.grid.columns.${isMetric(unitsInHeader) ? metric : imperial}`)
  );

  return [{
    headerName   : translate('tracking:tableView.grid.columns.group'),
    field        : 'accountName',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.asset'),
    field        : 'assetNameUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams,
    cellRenderer : 'assetName'
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.deviceName'),
    field        : 'deviceNameUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : !includesAssets && includesScDevices,
    filterParams : defaultCVSFilterParams,
    cellRenderer : 'assetName'
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.deviceType'),
    field        : 'deviceType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.time'),
    field        : 'eventTimeUI',
    cellRenderer : 'eventTimeUI',
    sortable     : true,
    filter       : 'date',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultDateFilterParams,
    comparator   : dateSortComparator
  },
  {
    headerName : translate('tracking:tableView.grid.columns.latestEvent'),
    field      : 'latestEventUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.eventType'),
    field        : 'eventTypeUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.address'),
    field        : 'addressUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.geofenceLocation'),
    field        : 'location',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.state'),
    field        : 'stateUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : caseInSensitiveEqualsFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.assignments'),
    field      : 'associations',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedKmph', 'speedMph'),
    field              : 'speedUI',
    cellRenderer       : 'speedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerKm', 'odometerMi'),
    field      : 'odometerUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHours'),
    field      : 'engineHourUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.identifierType'),
    field        : 'deviceIdType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.esn'),
    field        : 'deviceEsnUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.alias'),
    field        : 'assetAlias',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName         : translate('tracking:tableView.grid.columns.type'),
    field              : 'assetType',
    cellRenderer       : 'assetType',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.category'),
    field        : 'assetCategory',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.engineType'),
    field        : 'fuelTypeUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driver'),
    field        : 'operatorNamesUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driverType'),
    field        : 'operatorType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driverCategory'),
    field        : 'operatorCategory',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'postedSpeedLimitKm', 'postedSpeedLimit'),
    field      : 'postedSpeedLimit',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedGPSkmph', 'speedGPSmph'),
    field              : 'gpsSpeedUI',
    cellRenderer       : 'gpsSpeedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedVBkmph', 'speedVBmph'),
    field              : 'vbSpeedUI',
    cellRenderer       : 'vbSpeedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerGPSkm', 'odometerGPSmi'),
    field      : 'gpsOdometerUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerVBkm', 'odometerVBmi'),
    field      : 'vbOdometerUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHoursGPS'),
    field      : 'engineHoursGPSUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHoursVB'),
    field      : 'vbEngineHours',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.idleHours'),
    field      : 'idleHoursUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineLight'),
    field      : 'engineLightUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.fuelLevel'),
    field      : 'vbFuelPercent',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.battery'),
    field      : 'batteryUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.deviceBattery'),
    field      : 'batteryRemainingPercent',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.vbatInt'),
    field      : 'vbatIntUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.vbatExt'),
    field      : 'vbatExtUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.ignition'),
    field      : 'ignitionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input1'),
    field      : 'input1UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input2'),
    field      : 'input2UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input3'),
    field      : 'input3UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input4'),
    field      : 'input4UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input5'),
    field      : 'input5UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input6'),
    field      : 'input6UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input7'),
    field      : 'input7UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.inputMap'),
    field        : 'inputBitmap',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.gpsValidity'),
    field      : 'gpsValidityUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.commStateAvailable'),
    field      : 'commStateAvailableUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.latitude'),
    field      : 'latitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.longitude'),
    field      : 'longitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'altitudeM', 'altitude'),
    field      : 'altitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.routeName'),
    field        : 'routeNameUI',
    sortable     : true,
    resizable    : true,
    filter       : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.eventSequence'),
    field      : 'deviceMessageSequenceNumber',
    sortable   : true,
    resizable  : true,
    filter     : false,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineRpm'),
    field      : 'VBEngineSpeed',
    sortable   : true,
    resizable  : true,
    filter     : false,
    unSortIcon : true,
    hide       : true
  }];
};

/**
   * @getColumnDefinitionsMobile
   *
   * Returns column definitions for mobile grid implementation ...
   */
const getColumnDefinitionsMobile = (unitsInHeader: Unit): ColDef[] => {
  const getUOMColumn = (units: Unit, metric: string, imperial: string): string => (
    translate(`tracking:tableView.grid.columns.${isMetric(unitsInHeader) ? metric : imperial}`)
  );

  return [{
    headerName   : translate('tracking:tableView.grid.columns.group'),
    field        : 'accountName',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.asset'),
    field        : 'assetNameUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams,
    cellRenderer : 'assetName'
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.deviceName'),
    field        : 'deviceNameUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams,
    cellRenderer : 'assetName'
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.deviceType'),
    field        : 'deviceType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.time'),
    field        : 'eventTimeUI',
    cellRenderer : 'eventTimeUI',
    sortable     : true,
    filter       : 'date',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultDateFilterParams,
    comparator   : dateSortComparator
  },
  {
    headerName : translate('tracking:tableView.grid.columns.latestEvent'),
    field      : 'latestEventUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.eventType'),
    field        : 'eventTypeUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.address'),
    field        : 'addressUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.geofenceLocation'),
    field        : 'location',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.state'),
    field        : 'stateUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : caseInSensitiveEqualsFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.assignments'),
    field      : 'associations',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName         : translate('tracking:tableView.grid.columns.speedMph'),
    field              : 'speedUI',
    cellRenderer       : 'speedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : false
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerKm', 'odometerMi'),
    field      : 'odometerUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHours'),
    field      : 'engineHourUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.identifierType'),
    field        : 'deviceIdType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.esn'),
    field        : 'deviceEsnUI',
    sortable     : false,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.alias'),
    field        : 'assetAlias',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName         : translate('tracking:tableView.grid.columns.type'),
    field              : 'assetType',
    cellRenderer       : 'assetType',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : 'text',
    resizable          : true,
    unSortIcon         : true,
    hide               : false,
    filterParams       : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.category'),
    field        : 'assetCategory',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.engineType'),
    field        : 'fuelTypeUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driver'),
    field        : 'operatorNamesUI',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driverType'),
    field        : 'operatorType',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.driverCategory'),
    field        : 'operatorCategory',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'postedSpeedLimitKm', 'postedSpeedLimit'),
    field      : 'postedSpeedLimit',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedKmph', 'speedMph'),
    field              : 'gpsSpeedUI',
    cellRenderer       : 'gpsSpeedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName         : getUOMColumn(unitsInHeader, 'speedVBkmph', 'speedVBmph'),
    field              : 'vbSpeedUI',
    cellRenderer       : 'vbSpeedCell',
    cellRendererParams : ({data}) => ({data}),
    sortable           : true,
    filter             : false,
    resizable          : true,
    unSortIcon         : true,
    hide               : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerGPSkm', 'odometerGPSmi'),
    field      : 'gpsOdometerUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'odometerVBkm', 'odometerVBmi'),
    field      : 'vbOdometerUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHoursGPS'),
    field      : 'engineHoursGPSUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineHoursVB'),
    field      : 'vbEngineHours',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.idleHours'),
    field      : 'idleHoursUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineLight'),
    field      : 'engineLightUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.fuelLevel'),
    field      : 'vbFuelPercent',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.deviceBattery'),
    field      : 'batteryRemainingPercent',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.vbatInt'),
    field      : 'vbatIntUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.vbatExt'),
    field      : 'vbatExtUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.ignition'),
    field      : 'ignitionUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input1'),
    field      : 'input1UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input2'),
    field      : 'input2UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input3'),
    field      : 'input3UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input4'),
    field      : 'input4UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input5'),
    field      : 'input5UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input6'),
    field      : 'input6UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.input7'),
    field      : 'input7UI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.inputMap'),
    field        : 'inputBitmap',
    sortable     : true,
    filter       : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : true,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.gpsValidity'),
    field      : 'gpsValidityUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.commStateAvailable'),
    field      : 'commStateAvailableUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.latitude'),
    field      : 'latitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.longitude'),
    field      : 'longitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : getUOMColumn(unitsInHeader, 'altitudeM', 'altitude'),
    field      : 'altitudeUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName   : translate('tracking:tableView.grid.columns.routeName'),
    field        : 'routeNameUI',
    sortable     : true,
    resizable    : true,
    filter       : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultCVSFilterParams
  },
  {
    headerName : translate('tracking:tableView.grid.columns.eventSequence'),
    field      : 'deviceMessageSequenceNumber',
    sortable   : true,
    resizable  : true,
    filter     : false,
    unSortIcon : true,
    hide       : true
  },
  {
    headerName : translate('tracking:tableView.grid.columns.engineRpm'),
    field      : 'VBEngineSpeed',
    sortable   : true,
    resizable  : true,
    filter     : false,
    unSortIcon : true,
    hide       : true
  }];
};

export default memo(forwardRef(TrackingAssetServerGrid));
